// Central source of truth for portal-tab access.
// Each module has:
//   - id           : the permission key stored on the user record (must match backend KNOWN_MODULE_IDS)
//   - label / hint : shown in the Users page permission picker
//   - pathPrefixes : any URL that starts with one of these belongs to this module
//                    (used by ProtectedRoute and Hub/Sidebar filters)
//   - grants       : (optional) alternative permission ids that ALSO unlock this module.
//                    Used for scoped variants like "clients-add-only" — the user doesn't have
//                    full `clients` access but is still allowed to open the Clients page.
//
// Owner (info@d4pslogistics.com) ALWAYS has full access — permissions are ignored for them.
// A user with `permissions == null` (missing field) is treated as having full access,
// so existing users are never locked out on upgrade.

export const OWNER_EMAIL = "info@d4pslogistics.com";

export const MODULES = [
  { id: "jobs",       label: "Jobs",                          hint: "Deliveries, DSP grids, booking sheets", pathPrefixes: ["/jobs"] },
  { id: "clients",    label: "Clients",                       hint: "Clients & their sites",                  pathPrefixes: ["/clients"], grants: ["clients-add-only"] },
  { id: "suppliers",  label: "Subcontractors",                hint: "Subcontractors & self-employed drivers", pathPrefixes: ["/contacts"] },
  { id: "pos-sbs",    label: "Purchase Orders & Self-Bills",  hint: "Raise POs and self-bills",               pathPrefixes: ["/purchase-orders", "/self-bills"] },
  { id: "sub-inv",    label: "Subcontractor Invoices",        hint: "Match PO ↔ supplier invoice",            pathPrefixes: ["/subcontractor-invoices"] },
  { id: "finance",    label: "Finance",                       hint: "Chargeable & Payable",                   pathPrefixes: ["/finance"] },
  { id: "vehicles",   label: "D4PS Vehicles",                 hint: "Fleet, maintenance, incidents",          pathPrefixes: ["/vehicles"] },
  { id: "d4ps-docs",  label: "D4PS Certifications",           hint: "Insurance, VAT, certificates",           pathPrefixes: ["/d4ps-documents", "/documents"] },
  { id: "d4ps-insurance", label: "D4PS Insurances",           hint: "Insurance policies & renewals",          pathPrefixes: ["/d4ps-insurances"] },
  { id: "waste",      label: "Waste Transfer Notes",          hint: "Section 34 EPA compliance",              pathPrefixes: ["/waste-notes"] },
  { id: "reports",    label: "Reports",                       hint: "Monthly summary, coverage, dashboard",   pathPrefixes: ["/reports", "/dashboard"], grants: ["reports-jobs", "reports-coverage", "reports-client-profile", "reports-contact-profile", "reports-profit"] },
  { id: "notes",      label: "Notes",                         hint: "Reminders & jottings",                   pathPrefixes: ["/notes"] },
  { id: "hr",         label: "HR",                            hint: "Driver documents & licence checks",      pathPrefixes: ["/hr"] },
  { id: "policies",   label: "Policies",                      hint: "Company policies & review dates",        pathPrefixes: ["/policies"] },
  { id: "procedures", label: "Procedures",                    hint: "Operating procedures & review dates",    pathPrefixes: ["/procedures"] },
  { id: "settings",   label: "Settings",                      hint: "Company settings & user management",     pathPrefixes: ["/settings"] },
];

// Scoped/limited variants of a module. Shown in the Users picker as a sub-option
// under their parent module so it's clear these grant access but with restrictions.
export const SUB_PERMISSIONS = [
  {
    id: "clients-add-only",
    parent: "clients",
    label: "Clients — Add new only",
    hint: "Can open the Clients page and add new clients, but cannot see the existing client list.",
  },
  // ── Reports section sub-permissions ────────────────────────────────
  // Granting any of these ALSO unlocks the Reports page. When a user has
  // NONE of these but has parent `reports`, they see every section (legacy).
  {
    id: "reports-jobs",
    parent: "reports",
    label: "Reports — Jobs report",
    hint: "Download the daily/weekly/monthly jobs report PDF.",
  },
  {
    id: "reports-coverage",
    parent: "reports",
    label: "Reports — Contact coverage",
    hint: "Download subcontractor / self-employed coverage reports.",
  },
  {
    id: "reports-client-profile",
    parent: "reports",
    label: "Reports — Client profile",
    hint: "Download the A-Z client profile PDF / xlsx.",
  },
  {
    id: "reports-contact-profile",
    parent: "reports",
    label: "Reports — Sub / Driver profile",
    hint: "Download the A-Z contact profile PDF / xlsx.",
  },
  {
    id: "reports-profit",
    parent: "reports",
    label: "Reports — Profit reports",
    hint: "Access the Profit reports page (weekly, scoped, and full).",
  },
];

export const ALL_MODULE_IDS = MODULES.map((m) => m.id);
export const ALL_PERMISSION_IDS = [...ALL_MODULE_IDS, ...SUB_PERMISSIONS.map((s) => s.id)];

export function isOwner(user) {
  return (user?.email || "").toLowerCase() === OWNER_EMAIL;
}

/** True when the user is allowed to visit / see the given module.
 *  Honours `grants`: e.g. "clients-add-only" also unlocks the "clients" module page. */
export function hasAccess(user, moduleId) {
  if (!user) return false;
  if (isOwner(user)) return true;
  const perms = user.permissions;
  if (perms == null) return true;       // legacy / unset = full access
  if (perms.includes(moduleId)) return true;
  const mod = MODULES.find((m) => m.id === moduleId);
  return !!mod?.grants?.some((g) => perms.includes(g));
}

/** True when the user was explicitly granted this specific permission id.
 *  Use this for capability checks (e.g. "can edit clients" vs "add-only"). */
export function hasPermission(user, permId) {
  if (!user) return false;
  if (isOwner(user)) return true;
  const perms = user.permissions;
  if (perms == null) return true;
  return perms.includes(permId);
}

/** Map a URL path to a module id, or null if the path isn't gated. */
export function moduleForPath(pathname) {
  if (!pathname) return null;
  // Longest-prefix wins (so /d4ps-documents doesn't accidentally match /documents).
  let best = null;
  let bestLen = -1;
  for (const m of MODULES) {
    for (const p of m.pathPrefixes) {
      if ((pathname === p || pathname.startsWith(p + "/")) && p.length > bestLen) {
        best = m.id;
        bestLen = p.length;
      }
    }
  }
  return best;
}

/** True when the user is allowed to visit this URL path. Ungated paths always pass. */
export function canVisitPath(user, pathname) {
  const mod = moduleForPath(pathname);
  if (mod == null) return true;
  return hasAccess(user, mod);
}
